import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Camera, Check, X, ChevronRight, ChevronLeft, FileCheck2 } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";

export default function PDI() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [sess, setSess] = useState(null);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [photoB64, setPhotoB64] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { load(); }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/user/pdi/sessions/${sessionId}`);
      setSess(data);
      const firstPending = data.checkpoints.findIndex((c) => !c.result);
      setIdx(firstPending >= 0 ? firstPending : 0);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Session unavailable");
      nav("/dashboard");
    } finally { setLoading(false); }
  }

  const current = sess?.checkpoints?.[idx];
  const total = sess?.checkpoints?.length || 0;
  const completed = useMemo(() => sess?.checkpoints?.filter((c) => c.result).length || 0, [sess]);
  const progress = total ? Math.round((completed / total) * 100) : 0;

  useEffect(() => {
    if (current) {
      setNotes(current.notes || "");
      setPreviewUrl(current.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${current.photo_url}` : null);
      setPhotoB64(null);
    }
  }, [idx, sess]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onPickFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Photo too large (max 3MB)"); return; }
    // Downscale to keep under 2MB and speed uploads
    const dataUrl = await downscaleImage(f, 1600);
    setPreviewUrl(dataUrl);
    setPhotoB64(dataUrl.split(",", 2)[1]);
  }

  async function save(result) {
    if (!current) return;
    setSaving(true);
    try {
      await api.patch(`/user/pdi/sessions/${sessionId}/checkpoint`, {
        key: current.key,
        result,
        notes,
        photo_b64: photoB64 || undefined,
      });
      // update local
      const next = { ...sess };
      next.checkpoints = next.checkpoints.map((c) =>
        c.key === current.key ? { ...c, result, notes, photo_url: photoB64 ? `/api/user/pdi/sessions/${sessionId}/photo/${current.key}` : c.photo_url } : c
      );
      setSess(next);
      // advance
      if (idx < total - 1) setIdx(idx + 1);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    } finally { setSaving(false); }
  }

  async function finish() {
    try {
      await api.post(`/user/pdi/sessions/${sessionId}/complete`);
      toast.success("PDI report generated and saved to your Documents.");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    }
  }

  if (loading || !sess) {
    return <main className="min-h-screen bg-void grid place-items-center text-silver">Loading…</main>;
  }

  const category = current?.category || "";

  return (
    <main className="min-h-screen bg-void text-silver" data-testid="pdi-page">
      <header className="border-b border-white/5 sticky top-0 bg-void/85 backdrop-blur-md z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-3">
          <button onClick={() => nav("/dashboard")} className="inline-flex items-center gap-2 text-silver/80 hover:text-white text-sm" data-testid="pdi-back">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <div className="flex-1 max-w-md mx-4">
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
              <motion.div className="h-full gradient-bg" animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
            </div>
            <p className="mt-1 text-xs text-silver/60" data-testid="pdi-progress">{completed} of {total} · {progress}%</p>
          </div>
          <button
            onClick={finish}
            disabled={completed < total}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm gradient-bg text-white disabled:opacity-40 hover:scale-[1.02] transition-transform"
            data-testid="pdi-finish"
          >
            <FileCheck2 className="w-4 h-4" /> Generate Report
          </button>
        </div>
      </header>

      <section className="max-w-[900px] mx-auto px-5 md:px-10 py-10">
        <p className="eyebrow gradient-text">{category} · Checkpoint {idx + 1} of {total}</p>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl text-white leading-tight" data-testid="pdi-checkpoint-label">
          {current.label}
        </h1>

        <div className="mt-8 rounded-3xl border border-white/5 bg-surface p-6 md:p-8">
          <label className="block eyebrow gradient-text">Photo evidence</label>
          <div className="mt-3">
            {previewUrl ? (
              <div className="relative rounded-2xl overflow-hidden border border-white/10">
                <img src={previewUrl} alt="Checkpoint" className="w-full max-h-[420px] object-contain bg-black" data-testid="pdi-photo-preview" />
                <button
                  type="button"
                  onClick={() => { setPreviewUrl(null); setPhotoB64(null); if (fileRef.current) fileRef.current.value = ""; }}
                  className="absolute top-3 right-3 px-3 py-1.5 rounded-full text-xs bg-black/60 border border-white/10 hover:bg-black/80"
                  data-testid="pdi-photo-clear"
                >
                  Clear
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full h-40 rounded-2xl border border-dashed border-white/15 grid place-items-center gap-2 text-silver/70 hover:border-white/30 hover:text-white transition-colors"
                data-testid="pdi-photo-open"
              >
                <Camera className="w-5 h-5" />
                <span className="text-sm">Tap to capture / upload photo</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPickFile} className="hidden" data-testid="pdi-photo-input" />
          </div>

          <label className="block eyebrow gradient-text mt-8">Notes</label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What did you observe? (paint scuff, scratch, odd smell, warning light, etc.)"
            className="mt-3 bg-void border-white/10 text-white min-h-[100px]"
            data-testid="pdi-notes"
          />

          <div className="mt-8 flex gap-3">
            <button onClick={() => save("pass")} disabled={saving} className="flex-1 py-3.5 rounded-full text-sm font-medium text-white bg-emerald-500/20 border border-emerald-500/40 hover:bg-emerald-500/30 inline-flex items-center justify-center gap-2" data-testid="pdi-pass">
              <Check className="w-4 h-4" /> Pass
            </button>
            <button onClick={() => save("fail")} disabled={saving} className="flex-1 py-3.5 rounded-full text-sm font-medium text-white bg-red-500/20 border border-red-500/40 hover:bg-red-500/30 inline-flex items-center justify-center gap-2" data-testid="pdi-fail">
              <X className="w-4 h-4" /> Fail
            </button>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setIdx(Math.max(0, idx - 1))}
            disabled={idx === 0}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm border border-white/10 text-silver hover:bg-white/5 disabled:opacity-40"
            data-testid="pdi-prev"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <div className="text-xs text-silver/50">{sess.car} · {sess.location}</div>
          <button
            onClick={() => setIdx(Math.min(total - 1, idx + 1))}
            disabled={idx >= total - 1}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm border border-white/10 text-silver hover:bg-white/5 disabled:opacity-40"
            data-testid="pdi-next"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-10 rounded-3xl border border-white/5 bg-surface p-4 md:p-6" data-testid="pdi-checklist-grid">
          <p className="eyebrow gradient-text">All checkpoints</p>
          <div className="mt-3 grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 gap-2">
            {sess.checkpoints.map((c, i) => (
              <button
                key={c.key}
                onClick={() => setIdx(i)}
                className={`h-9 rounded-lg text-xs font-mono border ${
                  i === idx ? "border-transparent gradient-bg text-white" :
                  c.result === "pass" ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" :
                  c.result === "fail" ? "border-red-500/40 bg-red-500/15 text-red-300" :
                  "border-white/10 text-silver/60 hover:bg-white/5"
                }`}
                data-testid={`pdi-jump-${c.key}`}
                title={c.label}
              >
                {c.key}
              </button>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

async function downscaleImage(file, maxDim = 1600) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
